/**
 * useSuiteBridge — handles postMessage bridge for Suite v2 methods:
 * inject-studio, save-draft, inject-seed, get-context.
 *
 * Follows the exact pattern of useSunoIntegration.ts (lines 66-131).
 */

import { useEffect } from 'react';
import type { HookhouseFormData } from '@/types';
import type { RiffWriterSeedData } from '@/context/StudioFormContext';
import type {
  SuiteBridgeMessageType,
  InjectStudioPayload,
  SaveDraftPayload,
  InjectSeedPayload,
  PersonaIdentityPayload,
  ContextResponse,
} from '@/types/suite-bridge.types';
import { PAYLOAD_SCHEMAS } from '@/types/suite-bridge.schemas';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

interface UseSuiteBridgeOptions {
  loadFormData: (data: Partial<HookhouseFormData>) => void;
  loadFromRiffWriter: (data: RiffWriterSeedData) => void;
  formData: HookhouseFormData;
  activeTab: string;
  handleTabChange: (tab: string) => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  onReceivePersonaIdentity?: (payload: PersonaIdentityPayload) => void;
}

const ALLOWED_TYPES: SuiteBridgeMessageType[] = [
  'suite:inject-studio',
  'suite:save-draft',
  'suite:inject-seed',
  'suite:get-context',
  'suite:send-persona-identity',
];

export function useSuiteBridge({
  loadFormData,
  loadFromRiffWriter,
  formData,
  activeTab,
  handleTabChange,
  showToast,
  onReceivePersonaIdentity,
}: UseSuiteBridgeOptions): void {
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      const { type, id, payload } = event.data || {};
      if (!type || !id || !ALLOWED_TYPES.includes(type)) return;
      if (event.origin !== window.location.origin) return;

      const reply = (data: unknown, error?: string) => {
        const source = event.source as Window | null;
        if (source) {
          source.postMessage({ type: `${type}-result`, id, data, error }, event.origin);
        }
      };

      // Validate payload against the schema for this message type before
      // touching application state. Reject malformed payloads with a
      // structured error reply and a single-line server-style log.
      const schema = PAYLOAD_SCHEMAS[type as keyof typeof PAYLOAD_SCHEMAS];
      const parsed = schema.safeParse(payload);
      if (!parsed.success) {
        const issues = parsed.error.issues.map(i => `${i.path.join('.') || '<root>'}: ${i.message}`).join('; ');
        console.error(`[Suite Bridge v2] Invalid ${type} payload — ${issues}`);
        reply(null, `Invalid payload: ${issues}`);
        return;
      }

      try {
        switch (type) {
          case 'suite:inject-studio': {
            const data = payload as InjectStudioPayload;
            loadFormData(data);
            reply({ success: true });
            handleTabChange('studio');
            showToast('Studio updated from Suite app.', 'success');
            break;
          }

          case 'suite:save-draft': {
            const data = payload as SaveDraftPayload;
            const res = await fetchWithCsrf('/api/drafts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                title: data.title,
                artist: data.artist || '',
                style: data.style || '',
                excludedStyle: data.excludedStyle || '',
                lyricsWithTags: data.lyricsWithTags || '',
                sourceType: data.sourceType || 'suite',
                tags: data.tags || '',
              }),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.error || `Draft save failed (${res.status})`);
            }
            const draft = await res.json();
            showToast(`Draft "${data.title}" saved to Vault.`, 'success');
            reply(draft);
            break;
          }

          case 'suite:inject-seed': {
            const data = payload as InjectSeedPayload;
            const seedData: RiffWriterSeedData = {
              title: data.title,
              concept: data.concept,
              povSetting: data.povSetting || '',
              mustHaves: data.mustHaves || [],
              avoid: data.avoid || [],
              instruments: data.instruments || [],
              structure: '',
              bpm: data.bpm || 0,
              key: data.key || '',
            };
            loadFromRiffWriter(seedData);
            reply({ success: true });
            handleTabChange('studio');
            showToast(`Seed "${data.title}" loaded into Studio.`, 'success');
            break;
          }

          case 'suite:get-context': {
            const provider = localStorage.getItem('llm_provider') || 'gemini';
            const modelKey = `${provider === 'claude' ? 'claude' : provider}_model`;
            const model = localStorage.getItem(modelKey) || '';
            const context: ContextResponse = {
              formData,
              activeTab,
              provider,
              model,
            };
            reply(context);
            break;
          }

          case 'suite:send-persona-identity': {
            const data = payload as PersonaIdentityPayload;
            if (onReceivePersonaIdentity) {
              onReceivePersonaIdentity(data);
              reply({ success: true });
              showToast(`Received identity from ${data.name}. Opening Persona Wizard...`, 'success');
            } else {
              reply(null, 'Persona Wizard not available');
            }
            break;
          }
        }
      } catch (err: unknown) {
        console.error(`[Suite Bridge v2] Error handling ${type}:`, err);
        reply(null, err instanceof Error ? err.message : 'Unknown error');
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [loadFormData, loadFromRiffWriter, formData, activeTab, handleTabChange, showToast, onReceivePersonaIdentity]);
}
